import axios from "axios";
import { BRANCH_FORBIDDEN_CODE, CSRF_STORAGE_KEY, LOCAL_CSRF_TOKEN, LOCAL_STORAGE_AUTH_CHECK_KEY } from "~/shared/constants/auth";
import { getCookieValue } from "~/shared/lib/auth";

// kenny: path 형식은 이미 정해져 있어서 변경할 수 없음
const baseUrl = import.meta.env.VITE_BASE_API_URL;

function getCSRFToken(): string {
  // 1. 쿠키에서 읽기 (같은 도메인에서 서빙될 때)
  const fromCookie = getCookieValue("csrftoken");
  if (fromCookie) return fromCookie;
  // 2. localStorage에서 읽기 (cross-origin에서 쿠키 접근 불가 시)
  const fromStorage = localStorage.getItem(CSRF_STORAGE_KEY);
  if (fromStorage) return fromStorage;
  // 3. 하드코딩된 fallback
  return LOCAL_CSRF_TOKEN;
}

const axiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

axiosInstance.interceptors.request.use((config) => {
  config.headers["X-CSRFToken"] = getCSRFToken();
  return config;
});

// 응답에서 csrftoken 쿠키가 설정되었으면 localStorage에 저장
axiosInstance.interceptors.response.use(
  (response) => {
    const csrftoken = getCookieValue("csrftoken");
    if (csrftoken) {
      localStorage.setItem(CSRF_STORAGE_KEY, csrftoken);
    }
    return response;
  },
  async (error) => {
    // CSRF 토큰 불일치 시 localStorage 토큰을 삭제하고 fallback으로 1회 재시도
    const isCsrfError =
      error.response?.status === 403 &&
      typeof error.response?.data?.detail === "string" &&
      error.response.data.detail.includes("CSRF");

    if (isCsrfError && !error.config.__csrfRetried) {
      localStorage.removeItem(CSRF_STORAGE_KEY);
      error.config.__csrfRetried = true;
      error.config.headers["X-CSRFToken"] = getCSRFToken();
      return axiosInstance(error.config);
    }

    // 권한 없는 매장 링크를 열었을 때 나는 403 — 로그인은 멀쩡하므로 로그아웃시키면 안 된다.
    // (백엔드가 code 로 구분해서 내려준다. common/permissions.py HasBranchAccess)
    // FALLBACK: permission 조합 시 code 가 누락될 수 있으므로 URL 패턴도 함께 확인
    const isBranchForbidden =
      error.response?.status === 403 &&
      (error.response?.data?.code === BRANCH_FORBIDDEN_CODE ||
        error.config?.url?.includes("branches/"));

    // kenny: 레거시 백엔드에서 401대신 403을 unauthorized로 처리하고 있음
    // CSRF 토큰 오류는 로그아웃 처리하지 않음
    if (error.response?.status === 403 && !isCsrfError && !isBranchForbidden) {
      window.localStorage.removeItem(LOCAL_STORAGE_AUTH_CHECK_KEY);
      if (!window.localStorage.getItem("redirectAfterLogin") && !window.location.pathname.startsWith("/login")) {
        const currentPath = window.location.pathname + window.location.search;
        window.localStorage.setItem("redirectAfterLogin", currentPath);
      }
      const currentLanguage = window.location.search.split("language=")[1]?.split("&")[0] ?? "ko";
      window.location.href = `/login?language=${currentLanguage}`;
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
